import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SECTIONS = [
  { id: 'chats',       label: 'Chats',       emoji: '💬' },
  { id: 'groups',      label: 'Groups',      emoji: '👥' },
  { id: 'services',    label: 'Services',    emoji: '🤝' },
  { id: 'events',      label: 'Events',      emoji: '🗓️' },
  { id: 'marketplace', label: 'Marketplace', emoji: '🛒' },
];

type Actor = 'you' | 'system' | 'owner' | 'member' | 'provider' | 'seller' | 'organiser';

const ACTORS: Record<Actor, { bg: string; color: string; label: string; icon: string }> = {
  you:       { bg: '#EEF2FF', color: '#4F46E5', label: 'You',       icon: '👤' },
  system:    { bg: '#F3F4F6', color: '#6B7280', label: 'System',    icon: '⚙️' },
  owner:     { bg: '#FFFBEB', color: '#D97706', label: 'Owner',     icon: '👑' },
  member:    { bg: '#F5F3FF', color: '#7C3AED', label: 'Member',    icon: '👥' },
  provider:  { bg: '#FDF2F8', color: '#BE185D', label: 'Provider',  icon: '🤝' },
  seller:    { bg: '#ECFDF5', color: '#059669', label: 'Seller',    icon: '🏷️' },
  organiser: { bg: '#FFF7ED', color: '#C2410C', label: 'Organiser', icon: '🗓️' },
};

/* ─── Flow primitives ─────────────────────────────────────────────── */

const FlowStep: React.FC<{
  icon: string; title: string; desc: string; actor: Actor; tip?: string;
}> = ({ icon, title, desc, actor, tip }) => {
  const a = ACTORS[actor];
  return (
    <div className="flex gap-4">
      <div
        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-lg border-2"
        style={{ background: a.bg, borderColor: a.color + '44' }}
      >
        {icon}
      </div>
      <div className="flex-1 pb-1">
        <span
          className="inline-block text-xs font-bold px-2 py-0.5 rounded-full uppercase tracking-wide mb-1.5"
          style={{ background: a.bg, color: a.color }}
        >
          {a.icon} {a.label}
        </span>
        <div className="font-bold text-gray-900 text-sm mb-1">{title}</div>
        <div className="text-gray-500 text-sm leading-relaxed">{desc}</div>
        {tip && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 leading-relaxed">
            💡 {tip}
          </div>
        )}
      </div>
    </div>
  );
};

const Arrow = () => (
  <div className="flex flex-col items-start pl-5 my-0.5">
    <div className="w-0.5 h-5 bg-gray-200" />
    <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '7px solid #D1D5DB', marginLeft: '-4px' }} />
  </div>
);

const FlowBranch: React.FC<{
  label: string;
  left: { icon: string; label: string; color: string; bg: string; content: React.ReactNode };
  right: { icon: string; label: string; color: string; bg: string; content: React.ReactNode };
}> = ({ label, left, right }) => (
  <div>
    <div className="pl-5 mb-2"><div className="w-0.5 h-5 bg-gray-200" /></div>
    <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl px-4 py-2.5 mb-3 text-center text-xs font-semibold text-gray-400">
      ◆ {label}
    </div>
    <div className="flex gap-4 items-start">
      <div className="flex-1 pt-3.5" style={{ borderTop: `3px solid ${left.color}` }}>
        <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: left.bg, color: left.color }}>
          {left.icon} {left.label}
        </span>
        {left.content}
      </div>
      <div className="flex-1 pt-3.5" style={{ borderTop: `3px solid ${right.color}` }}>
        <span className="inline-block text-xs font-bold px-2.5 py-1 rounded-full mb-3" style={{ background: right.bg, color: right.color }}>
          {right.icon} {right.label}
        </span>
        {right.content}
      </div>
    </div>
  </div>
);

const JourneyTitle: React.FC<{ emoji: string; title: string; subtitle: string }> = ({ emoji, title, subtitle }) => (
  <div className="mb-8">
    <div className="flex items-center gap-3 mb-2">
      <span className="text-3xl">{emoji}</span>
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
    <p className="text-gray-500 leading-relaxed ml-1">{subtitle}</p>
  </div>
);

const JourneySection: React.FC<{ label: string; subtitle?: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ label, subtitle, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-8">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-left bg-indigo-50 border-l-4 border-indigo-500 rounded-r-xl px-4 py-3 cursor-pointer font-inherit"
        style={{ marginBottom: open ? '16px' : 0 }}
      >
        <div>
          <div className="text-sm font-bold text-indigo-700">{label}</div>
          {subtitle && <div className="text-xs text-gray-400 mt-0.5 font-normal">{subtitle}</div>}
        </div>
        <span
          className="text-indigo-400 text-xs ml-2 flex-shrink-0 inline-block transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >▼</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
};

const Divider = () => <hr className="border-0 border-t border-gray-100 my-10" />;

const ReportSafety = () => (
  <JourneySection label="🛡️ Report & Safety" subtitle="Flag content or block users to keep the community safe">
    <FlowStep actor="you" icon="🚫" title="Long-press any content"
      desc="Long-press a message, listing, or profile to see reporting options." />
    <Arrow />
    <FlowStep actor="you" icon="🚨" title="Report or block"
      desc="Tap 'Report' to flag inappropriate content, or 'Block User' to stop receiving messages from them." />
    <Arrow />
    <FlowStep actor="system" icon="🛡️" title="Action taken"
      desc="Reported content is reviewed. Blocked users can no longer message you or see your profile." />
  </JourneySection>
);

const AlsoSection: React.FC<{ items: { icon: string; title: string; desc: string }[] }> = ({ items }) => (
  <JourneySection label="📌 Also in this tab" subtitle="Other handy features you can access here">
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
      {items.map((item, i) => (
        <div key={i} className="bg-gray-50 border border-gray-100 rounded-2xl p-4 shadow-sm">
          <div className="text-2xl mb-2">{item.icon}</div>
          <div className="font-bold text-gray-900 mb-1 text-sm">{item.title}</div>
          <div className="text-xs text-gray-500 leading-relaxed">{item.desc}</div>
        </div>
      ))}
    </div>
  </JourneySection>
);

/* ─── Section content ───────────────────────────────────────────────── */

const ChatsContent = () => (
  <div>
    <JourneyTitle emoji="💬" title="Chats" subtitle="Your communication hub — all group conversations, direct messages, and the AI assistant live here." />
    <JourneySection label="🐝 Commy AI — your community assistant" subtitle="Ask anything about your community in any language">
      <FlowStep actor="you" icon="🐝" title="Open the Commy AI chat" desc="Tap the Commy AI conversation at the top of your Chats list." />
      <Arrow />
      <FlowStep actor="you" icon="💬" title="Ask anything" desc="Type a question in any language — about events, services, groups, or anything happening in your community." />
      <Arrow />
      <FlowStep actor="system" icon="✨" title="Get an instant answer" desc="Commy searches your community's data and replies with relevant results, links, and suggestions." />
    </JourneySection>
    <JourneySection label="❤️ Share & Care" subtitle="Replace doomscrolling with curated content from your community">
      <FlowStep actor="you" icon="❤️" title="Open Share & Care" desc="Tap the Share & Care conversation in your Chats list." />
      <Arrow />
      <FlowStep actor="you" icon="📖" title="Browse tips & recommendations" desc="A curated feed of helpful tips, local recommendations, and community wisdom — contributed by members and AI. No ads, no noise." />
    </JourneySection>
    <JourneySection label="📤 Sending a message" subtitle="Chat with groups or individuals in real time">
      <FlowStep actor="you" icon="📱" title="Open the Chats tab" desc="The Chats tab is your home screen. All your active conversations appear here, most recent at the top." />
      <Arrow />
      <FlowStep actor="you" icon="🔍" title="Find your conversation" desc="Tap a conversation from the list, or use the search bar to find a message by keyword across all chats." />
      <Arrow />
      <FlowStep actor="you" icon="✍️" title="Type your message" desc="Tap the message box at the bottom of the screen and type what you want to say." />
      <Arrow />
      <FlowStep actor="you" icon="📤" title="Tap send" desc="Tap the paper plane icon to send. To reply privately to someone in a group, long-press their message and tap 'Reply Privately'." tip="Long-press any message to react with an emoji, copy it, or report it." />
      <Arrow />
      <FlowStep actor="system" icon="✅" title="Message delivered" desc="Your message is instantly delivered to all members of the conversation." />
    </JourneySection>
    <ReportSafety />
    <AlsoSection items={[
      { icon: '✏️', title: 'Create from chat', desc: 'Tap the pen icon in a group chat to create events, services, or listings linked to that group.' },
    ]} />
  </div>
);

const GroupsContent = () => (
  <div>
    <JourneyTitle emoji="👥" title="Groups" subtitle="Connect with people who share your interests. Find an existing group or create your own." />
    <JourneySection label="✉️ Joining a group" subtitle="Find and request to join community groups">
      <FlowStep actor="you" icon="🗂️" title="Open the Groups tab" desc="Tap Groups in the bottom navigation bar." />
      <Arrow />
      <FlowStep actor="you" icon="🔍" title="Browse or search" desc="Scroll the category filter (Sports, Food, Hobbies…) or type in the search bar. Use the sort bar to order by Most Members, Newest, or Distance." />
      <Arrow />
      <FlowStep actor="you" icon="👁️" title="View group details" desc="Tap any group card to see the full description, member count, location, and photos." />
      <Arrow />
      <FlowStep actor="you" icon="✉️" title="Send a join request" desc="Tap 'Join Group' at the bottom. Write a short message explaining why you'd like to join (members of the group see this), then tap 'Send Request'." tip="A personal intro message significantly increases your chances of being approved." />
      <Arrow />
      <FlowStep actor="system" icon="🔔" title="Members are notified" desc="Existing group members receive a notification with your request and message." />
      <Arrow />
      <FlowStep actor="member" icon="👀" title="A member reviews your request" desc="Any existing member can read your message and decide to approve or reject." />
      <FlowBranch
        label="Member's decision"
        left={{ icon: '✅', label: 'Approved', color: '#059669', bg: '#ECFDF5', content: (
          <div>
            <FlowStep actor="system" icon="🔔" title="You get notified" desc="You receive a push notification that your request was approved." />
            <Arrow />
            <FlowStep actor="you" icon="💬" title="Group chat appears in Chats" desc="The group's conversation is now visible in your Chats tab." />
            <Arrow />
            <FlowStep actor="you" icon="🎉" title="Start participating!" desc="Send messages, react to posts, and connect with fellow members." />
          </div>
        )}}
        right={{ icon: '❌', label: 'Rejected', color: '#DC2626', bg: '#FEF2F2', content: (
          <div>
            <FlowStep actor="system" icon="🔔" title="You get notified" desc="You receive a notification that your request was not approved." />
            <Arrow />
            <FlowStep actor="you" icon="🔄" title="Try another group" desc="Browse other groups in the same category, or create your own." />
          </div>
        )}}
      />
    </JourneySection>
    <JourneySection label="🔒 Joining a private group" subtitle="Use a Group ID to find invite-only groups">
      <FlowStep actor="you" icon="🔒" title="Tap the lock icon" desc="Tap the 🔒 icon in the top-right of the Groups screen." />
      <Arrow />
      <FlowStep actor="you" icon="🔑" title="Enter the Group ID" desc="Type the Group ID shared by the owner, then tap 'Find Group'." />
      <Arrow />
      <FlowStep actor="you" icon="✉️" title="Send your join request" desc="Continue the same flow as a public group from here." />
    </JourneySection>
    <JourneySection label="➕ Creating a group" subtitle="Start your own community group in minutes">
      <FlowStep actor="you" icon="➕" title="Tap the + button" desc="Tap the + icon in the top-right of the Groups screen." />
      <Arrow />
      <FlowStep actor="you" icon="📝" title="Fill in the details" desc="Enter a name, description (use 'Use AI' for help), location, and category. Choose 'Community Group' (public) or 'Private Group' (invite-only)." />
      <Arrow />
      <FlowStep actor="you" icon="📸" title="Add photos & publish" desc="Add up to 3 photos, optionally link a WhatsApp or Facebook group, then tap 'Create Group'." />
      <Arrow />
      <FlowStep actor="system" icon="🌐" title="Group is live" desc="Your group appears in search results for members in your community. Incoming join requests will appear on your group's detail page." />
    </JourneySection>
    <ReportSafety />
  </div>
);

const ServicesContent = () => (
  <div>
    <JourneyTitle emoji="🤝" title="Services" subtitle="Find local help or offer your skills to the community — from cleaning and tutoring to tech support." />
    <JourneySection label="🔍 Finding & contacting a provider" subtitle="Browse local services and message providers directly">
      <FlowStep actor="you" icon="🗂️" title="Open the Services tab" desc="Tap Services in the bottom navigation bar." />
      <Arrow />
      <FlowStep actor="you" icon="🔍" title="Filter by category" desc="Use the category bar (Cleaning, Tutoring, Babysitting…) or search by keyword. Sort by Newest or Distance." />
      <Arrow />
      <FlowStep actor="you" icon="👁️" title="View service details" desc="Tap a service card to see the full description, availability, location, distance, and photos." />
      <Arrow />
      <FlowStep actor="you" icon="💬" title="Tap 'Contact Provider'" desc="Tap the button at the bottom of the detail page (or ••• → 'Contact Owner')." />
      <Arrow />
      <FlowStep actor="system" icon="📩" title="Private chat opens" desc="A one-on-one chat with the service provider is created instantly." />
      <Arrow />
      <FlowStep actor="you" icon="📅" title="Discuss & arrange" desc="Chat with the provider to confirm details, availability, and pricing." />
      <Arrow />
      <FlowStep actor="provider" icon="✅" title="Provider confirms" desc="Once you agree, the provider confirms and you're all set." />
    </JourneySection>
    <JourneySection label="💬 Joining the service discussion" subtitle="Ask questions and read feedback from others">
      <FlowStep actor="you" icon="💬" title="Tap the chat icon" desc="On the service detail page, tap the 💬 icon in the top-right to open the group discussion for this service." />
      <Arrow />
      <FlowStep actor="you" icon="🗣️" title="Ask questions or share feedback" desc="Connect with others who've used or are interested in this service. The chat also appears in your Chats tab." />
    </JourneySection>
    <JourneySection label="📝 Posting your own service" subtitle="Offer your skills to the community">
      <FlowStep actor="you" icon="➕" title="Tap the + button" desc="Tap the + icon in the top-right of the Services screen." />
      <Arrow />
      <FlowStep actor="you" icon="📝" title="Fill in the details" desc="Add a title, description (use 'Use AI' for help), location, category, and up to 3 photos." />
      <Arrow />
      <FlowStep actor="you" icon="📤" title="Tap 'Add Service'" desc="Your listing goes live and is discoverable by members in your community." tip="A clear description and at least one photo will get you significantly more enquiries." />
      <Arrow />
      <FlowStep actor="system" icon="🌐" title="Service is live" desc="Community members can now find and contact you through your listing." />
    </JourneySection>
    <ReportSafety />
  </div>
);

const EventsContent = () => (
  <div>
    <JourneyTitle emoji="🗓️" title="Events" subtitle="Discover local events and set reminders so you never miss out — or organise your own." />
    <JourneySection label="🗓️ Discovering & attending an event" subtitle="Find local events and get directions">
      <FlowStep actor="you" icon="🗂️" title="Open the Events tab" desc="Tap Events in the bottom navigation bar." />
      <Arrow />
      <FlowStep actor="you" icon="🔍" title="Browse or filter" desc="Filter by category (Social, Sports, Workshop…) or sort by Date, Newest, or Distance. Search by keyword or location." />
      <Arrow />
      <FlowStep actor="you" icon="👁️" title="View event details" desc="Tap an event card to see the full date, time, description, location, and attendee count. Tap the address to open Maps." />
      <Arrow />
      <FlowStep actor="you" icon="🎉" title="Attend the event!" desc="If the event has tickets, tap 'Get Tickets' on the detail page to be taken to the ticketing page." />
    </JourneySection>
    <JourneySection label="🔔 Event reminders" subtitle="Set custom reminders plus automatic alerts at 12h and 6h before">
      <FlowStep actor="you" icon="🔔" title="Set a custom reminder" desc="On the event detail page, tap 'Remind Me' and choose when: 15 min, 30 min, 1 hr, 2 hrs, 1 day before — or pick a custom time. Add a personal note if you like." tip="You can set multiple reminders for the same event. View them under 'Remind Me' → 'My Reminders'." />
      <Arrow />
      <FlowStep actor="system" icon="✅" title="Reminder saved" desc="Your reminder is stored. You'll receive a push notification at your chosen time (with your optional note)." />
      <Arrow />
      <FlowStep actor="system" icon="📢" title="Automatic community reminders" desc="The system automatically sends reminders to the community channel 12 hours and 6 hours before the event starts — no action needed from you or the organiser." />
    </JourneySection>
    <JourneySection label="💬 Joining the event discussion" subtitle="Chat with attendees and the organiser">
      <FlowStep actor="you" icon="💬" title="Tap the chat icon" desc="On the event detail page, tap the 💬 icon in the top-right to open the group discussion for this event." />
      <Arrow />
      <FlowStep actor="you" icon="🗣️" title="Ask questions or coordinate" desc="Talk with other attendees, ask the organiser questions, or coordinate plans. The chat also appears in your Chats tab." />
    </JourneySection>
    <JourneySection label="➕ Creating an event" subtitle="Publish your own event for the community">
      <FlowStep actor="you" icon="➕" title="Tap the + button" desc="Tap the + icon in the top-right of the Events screen." />
      <Arrow />
      <FlowStep actor="you" icon="📝" title="Fill in the details" desc="Add title, description (use 'Use AI' for help), date, start time, location, category, and up to 3 photos. Optionally set an end time and ticket URL." />
      <Arrow />
      <FlowStep actor="you" icon="📤" title="Tap 'Add Event'" desc="Your event is published and visible to the community." />
      <Arrow />
      <FlowStep actor="system" icon="🌐" title="Event is live" desc="Community members can discover, attend, and set reminders for your event." />
      <Arrow />
      <FlowStep actor="organiser" icon="🔔" title="Members send join requests / questions" desc="Attendees can join the event discussion chat to reach you with questions." />
    </JourneySection>
    <ReportSafety />
  </div>
);

const MarketplaceContent = () => (
  <div>
    <JourneyTitle emoji="🛒" title="Marketplace" subtitle="Buy, sell, rent, or give away items within your community — local and trusted." />
    <JourneySection label="🛒 Finding & buying something" subtitle="Browse listings and contact sellers directly">
      <FlowStep actor="you" icon="🗂️" title="Open the Marketplace tab" desc="Tap Marketplace in the bottom navigation bar." />
      <Arrow />
      <FlowStep actor="you" icon="🔍" title="Browse or search" desc="Scroll the 2-column grid or filter by category (Housing, Electronics, Furniture…). Sort by Newest, Price, or Distance. Use search for keywords." />
      <Arrow />
      <FlowStep actor="you" icon="👁️" title="View listing details" desc="Tap a card to see all photos (swipe through the gallery), price, condition, description, and location." />
      <Arrow />
      <FlowStep actor="you" icon="💬" title="Tap 'Contact Seller'" desc="Tap the button at the bottom of the detail page (or ••• → 'Contact Owner')." />
      <Arrow />
      <FlowStep actor="system" icon="📩" title="Private chat opens" desc="A direct chat with the seller is created instantly." />
      <Arrow />
      <FlowStep actor="you" icon="🤝" title="Negotiate & arrange" desc="Agree on the final price, condition, and meetup details in the chat." tip="Listings with multiple clear photos receive far more interest. Try to include a photo showing the item's condition clearly." />
      <Arrow />
      <FlowStep actor="seller" icon="✅" title="Seller confirms" desc="Once agreed, meet up and complete the exchange." />
    </JourneySection>
    <JourneySection label="💬 Joining the listing discussion" subtitle="Ask questions publicly about a listing">
      <FlowStep actor="you" icon="💬" title="Tap the chat icon" desc="On the listing detail page, tap the 💬 icon in the top-right to open the group discussion for this listing." />
      <Arrow />
      <FlowStep actor="you" icon="🗣️" title="Ask questions publicly" desc="See what others are asking, negotiate openly, or hear from other interested buyers. The chat also appears in your Chats tab." />
    </JourneySection>
    <JourneySection label="🏷️ Posting a listing" subtitle="Sell, rent, or give away items to your community">
      <FlowStep actor="you" icon="➕" title="Tap the + button" desc="Tap the + icon in the top-right of the Marketplace screen." />
      <Arrow />
      <FlowStep actor="you" icon="🏷️" title="Choose listing type" desc="Select Sell, Rent, or Free. For Sell/Rent, choose your currency (USD, EUR, GBP, INR, CAD, AUD) and enter a price." />
      <Arrow />
      <FlowStep actor="you" icon="📝" title="Fill in the details" desc="Add title, description (use 'Use AI' for help), location, category, item condition, and up to 6 photos." />
      <Arrow />
      <FlowStep actor="you" icon="📤" title="Tap 'Create Listing'" desc="Your listing goes live and is visible to the community." />
      <Arrow />
      <FlowStep actor="system" icon="🌐" title="Listing is live" desc="Interested buyers in your community can now find and contact you." />
    </JourneySection>
    <ReportSafety />
  </div>
);

const SECTION_CONTENT: Record<string, React.ReactNode> = {
  chats: <ChatsContent />,
  groups: <GroupsContent />,
  services: <ServicesContent />,
  events: <EventsContent />,
  marketplace: <MarketplaceContent />,
};

/* ─── Main page ─────────────────────────────────────────────────────── */

const UserManual: React.FC = () => {
  const [active, setActive] = useState('chats');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sec = params.get('section');
    if (sec && SECTIONS.find(s => s.id === sec)) setActive(sec);
  }, []);

  const switchSection = (id: string) => {
    setActive(id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.replaceState(null, '', `/user-manual?section=${id}`);
  };

  const idx = SECTIONS.findIndex(s => s.id === active);
  const prev = idx > 0 ? SECTIONS[idx - 1] : null;
  const next = idx < SECTIONS.length - 1 ? SECTIONS[idx + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <div className="sticky top-0 z-10 bg-indigo-600 px-4 py-2 flex items-center gap-3">
        <Link to="/" className="text-white font-bold text-base tracking-tight flex-shrink-0">
          Sahayy
        </Link>
        {/* Section tabs */}
        <div className="flex-1 flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {SECTIONS.map(sec => (
            <button
              key={sec.id}
              onClick={() => switchSection(sec.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap flex-shrink-0 transition-colors ${
                active === sec.id
                  ? 'bg-white text-indigo-700'
                  : 'text-indigo-200 hover:text-white hover:bg-indigo-500'
              }`}
            >
              <span>{sec.emoji}</span>
              {sec.label}
            </button>
          ))}
        </div>
        <Link to="/" className="text-indigo-200 text-xs hover:text-white transition-colors flex-shrink-0">
          ← Home
        </Link>
      </div>

      {/* Hero */}
      <div className="bg-indigo-600 text-white px-6 py-10 text-center">
        <h1 className="text-3xl font-bold mb-2">How to use Sahayy</h1>
        <p className="text-indigo-200 max-w-md mx-auto text-sm leading-relaxed">
          Step-by-step user journeys for every part of the app.
        </p>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 pb-20">
        <div key={active}>
          {SECTION_CONTENT[active]}
        </div>

        <Divider />

        {/* Prev / Next */}
        <div className="flex justify-between items-center flex-wrap gap-3">
          {prev
            ? <button onClick={() => switchSection(prev.id)} className="border-2 border-indigo-500 text-indigo-600 font-semibold px-5 py-2.5 rounded-xl hover:bg-indigo-50 transition-colors text-sm">
                ← {prev.emoji} {prev.label}
              </button>
            : <div />}
          {next
            ? <button onClick={() => switchSection(next.id)} className="bg-indigo-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-indigo-500 transition-colors text-sm">
                Next: {next.emoji} {next.label} →
              </button>
            : <div />}
        </div>

        {/* Contact */}
        <div className="mt-12 bg-indigo-50 border border-indigo-100 rounded-2xl px-6 py-6 text-center">
          <p className="font-bold text-gray-900 mb-1">Still have questions?</p>
          <p className="text-gray-500 text-sm leading-relaxed">
            Reach out to us at{' '}
            <a href="mailto:care@sahayy.com" className="text-indigo-600 font-semibold hover:text-indigo-500">
              care@sahayy.com
            </a>
            {' '}and we'll be happy to help.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UserManual;
